import {
    FeedbacksTableData,
    FeedbacksTableFiltersApi,
} from '@/components/feedbacks-table/feedbacks-table-columns.types';
import { ClearFilterButton } from '@/components/feedbacks-table/feedbacks-table-columns.filters.shared';
import { ratingFilterValues } from '@/components/feedbacks-table/use-feedbacks-table-filters';
import { Marketplace } from '@/enums/marketplace';
import type { RuntimeSupportedMarketplace } from '@/shared/marketplace';
import { getReviewStatusFilterLabel, REVIEW_STATUS_FILTER_VALUES } from '@/shared/review-status-filter';
import DatePicker from '@/ui/DatePicker';
import Select from '@/ui/Select';

export const marketplaceOptions: Array<{ name: string; value: RuntimeSupportedMarketplace }> = [
    { name: 'WB', value: Marketplace.Wb },
    { name: 'Ozon', value: Marketplace.Ozon },
];

export const renderMetaFilterByColumn = (columnKey: keyof FeedbacksTableData, filters: FeedbacksTableFiltersApi) => {
    const {
        convertedStatus,
        createdDate,
        answerUpdatedDate,
        selectedRatings,
        selectedPlatforms,
        setRating,
        setConvertedStatus,
        setCreatedDate,
        setAnswerUpdatedDate,
        platform,
        setPlatform,
        onRatingFilterChange,
        onPlatformFilterChange,
    } = filters;

    switch (columnKey) {
        case 'createdDate':
            return (
                <div className="flex h-[34px] w-[62px] min-w-0 items-center gap-1 md:w-[72px]">
                    <DatePicker
                        id="reviews-table-filter-created-date"
                        aria-label="Фильтр даты отзыва"
                        placeholder="Дата"
                        selectedDate={createdDate}
                        setSelectedDate={setCreatedDate}
                        uiSize="compact"
                        className="h-[34px] min-w-0 flex-1 [&>button]:h-[34px] [&>button]:min-h-[34px]"
                    />
                    {createdDate && (
                        <ClearFilterButton ariaLabel="Очистить фильтр даты" onClick={() => setCreatedDate('')} />
                    )}
                </div>
            );
        case 'answer:updatedDate':
            return (
                <div className="flex h-[34px] w-[70px] min-w-0 items-center gap-1 md:w-[80px]">
                    <DatePicker
                        id="reviews-table-filter-answer-updated-date"
                        aria-label="Фильтр даты обновления ответа"
                        placeholder="Дата"
                        selectedDate={answerUpdatedDate}
                        setSelectedDate={setAnswerUpdatedDate}
                        uiSize="compact"
                        className="h-[34px] min-w-0 flex-1 [&>button]:h-[34px] [&>button]:min-h-[34px]"
                    />
                    {answerUpdatedDate && (
                        <ClearFilterButton
                            ariaLabel="Очистить фильтр даты ответа"
                            onClick={() => setAnswerUpdatedDate('')}
                        />
                    )}
                </div>
            );
        case 'convertedStatus':
            return (
                <div className="flex h-[34px] w-[60px] min-w-0 items-center gap-1 md:w-[70px]">
                    <Select
                        id="reviews-table-filter-status"
                        ariaLabel="Фильтр статуса отзыва"
                        options={REVIEW_STATUS_FILTER_VALUES.map((value) => ({
                            name: getReviewStatusFilterLabel(value),
                            value,
                        }))}
                        placeholder="Статус"
                        className="h-[34px] min-w-0 flex-1 [&>button]:h-[34px] [&>button]:min-h-[34px]"
                        value={convertedStatus}
                        onChange={setConvertedStatus}
                        uiSize="compact"
                    />
                    {convertedStatus && (
                        <ClearFilterButton
                            ariaLabel="Очистить фильтр статуса"
                            onClick={() => setConvertedStatus(undefined)}
                        />
                    )}
                </div>
            );
        case 'platform':
            return (
                <div
                    className={`flex h-[34px] min-w-0 items-center gap-1 ${selectedPlatforms.length ? 'w-[42px]' : 'w-[41px]'}`}
                >
                    <Select
                        id="reviews-table-filter-platform"
                        ariaLabel="Фильтр платформы"
                        options={marketplaceOptions}
                        placeholder=""
                        className={
                            selectedPlatforms.length
                                ? 'h-[34px] w-[24px] min-w-[24px] flex-none [&>button]:h-[34px] [&>button]:min-h-[34px] [&>button]:px-[4px] [&>button>div]:justify-center [&>button>div>span]:hidden'
                                : 'h-[34px] min-w-0 flex-1 [&>button]:h-[34px] [&>button]:min-h-[34px]'
                        }
                        values={selectedPlatforms}
                        onValuesChange={onPlatformFilterChange}
                        multiple
                        uiSize="compact"
                    />
                    {platform && (
                        <ClearFilterButton
                            ariaLabel="Очистить фильтр платформы"
                            onClick={() => setPlatform(undefined)}
                            className="!w-auto !px-0"
                        />
                    )}
                </div>
            );
        case 'rating':
            return (
                <div className="flex h-[34px] w-[80px] min-w-0 items-center gap-1">
                    <Select
                        id="reviews-table-filter-rating"
                        ariaLabel="Фильтр рейтинга отзыва"
                        options={ratingFilterValues.map((ratingValue) => ({
                            name: `${ratingValue}/5`,
                            value: ratingValue,
                        }))}
                        placeholder="Рейт."
                        className="h-[34px] min-w-0 flex-1 [&>button]:h-[34px] [&>button]:min-h-[34px]"
                        values={selectedRatings}
                        onValuesChange={onRatingFilterChange}
                        multiple
                        uiSize="compact"
                    />
                    {selectedRatings.length > 0 && (
                        <ClearFilterButton ariaLabel="Очистить фильтр рейтинга" onClick={() => setRating(undefined)} />
                    )}
                </div>
            );
        default:
            return null;
    }
};
